import { Box, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { produce } from 'immer';
import { createParser, Options, useQueryState } from 'nuqs';
import React, { useTransition } from 'react';

import { FeatureTypeList } from '@/services/filters/new-filters.service';

import { NewFilter } from '../new-filter/new-filter';
import { isFilterEqual, useClearSearch } from '../utils';

import styles from './new-features.module.scss';

export const NewFeatures: React.FC<{
  count: number;
  featureCounts: FeatureTypeList;
}> = ({ count, featureCounts }) => {
  const mappingFunction = <T extends { id: number }>(
    item: T,
  ): T & { isChecked: boolean } => {
    return {
      isChecked: false,
      ...item,
    };
  };

  const createMapParser = <T extends { id: number; isChecked: boolean }>(
    items: T[],
  ) => {
    return createParser({
      parse(queryValue) {
        return items.map((item) => {
          item.isChecked = queryValue.includes(item.id.toString());
          return item;
        });
      },
      serialize(value) {
        return value
          .filter((item) => item.isChecked)
          .map((item) => item.id)
          .join(',');
      },
      eq(a, b) {
        return isFilterEqual(a, b);
      },
    });
  };

  const [_, startTransition] = useTransition();

  const mappedFeatures = featureCounts.map(mappingFunction);
  const parseFeatures = createMapParser(mappedFeatures);
  const [queryFeatures, setQueryFeatures] = useQueryState(
    'features',
    parseFeatures
      .withDefault(mappedFeatures)
      .withOptions({ startTransition, shallow: false }),
  );

  const clearSearch = useClearSearch();

  const toggleFilter = async (
    id: number,
    setter: (
      value: any[] | ((old: any[]) => any[] | null) | null,
      options?: Options,
    ) => Promise<URLSearchParams>,
  ) => {
    clearSearch();
    await setter((options) => {
      return produce(options, (draftOptions) => {
        draftOptions?.forEach((option) => {
          if (option.id === id) {
            option.isChecked = !option.isChecked;
          }
        });
      });
    });
  };

  return (
    <NewFilter label="Features" placement="bottom-end" count={count}>
      <Box className={styles.featuresWrapper}>
        <Box className={styles.featuresList}>
          {queryFeatures.map((feature) => {
            return (
              <Box
                key={feature.id}
                onClick={() => {
                  if (feature && feature?.count > 0) {
                    toggleFilter(feature.id, setQueryFeatures);
                  }
                }}
                className={cn(
                  styles.featureItem,
                  feature.isChecked && styles.featureSelected,
                  featureCounts.find((item) => feature.id === item.id)
                    ?.count === 0 && styles.featureDisabled,
                )}
              >
                <Text className={styles.featureText}>
                  {feature.name}
                  <Text as="span" className={styles.filterCount}>
                    {` (${feature?.count})`}
                  </Text>
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>
    </NewFilter>
  );
};
